import React, { useCallback, useEffect, useRef, useState } from "react";
import "./tournament-round-create-edit.scss";

import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import {
  getTournament,
  getTournamentFormatTypes,
  selectCurrentTournament,
  selectTournamentFormatTypes,
} from "../../../reducers/tournamentSlice";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { GSSidePanelPage, GSForm } from "golfstatus_react_components";
import {
  getRoundDetailForm,
  isRoundComplete,
} from "../../../forms/TournamentRoundForms";
import {
  getLinkedRound,
  getNextRoundNumber,
  useLinkedRoundSeed,
} from "../../../hooks/round/roundHooks";
import {
  getFacilityCourses,
  selectCurrentFacility,
} from "../../../reducers/facilitySlice";
import {
  createRound,
  getRound,
  getTournamentRounds,
  saveRound,
  selectCurrentRound,
  selectLoading,
  selectRoundNotifications,
  selectTournamentRounds,
  setCurrentRound,
  setRoundNotifications,
} from "../../../reducers/roundSlice";
import {
  getSaveBannerActions,
  useNotificationAction,
  useNotificationBanner,
  useNotificationNavigation,
} from "../../../hooks/notificationHooks";
import { useFormValidation } from "../../../hooks/formHooks";

const TournamentRoundCreateEdit = (props) => {
  //state
  const [selectedFacility, setSelectedFacility] = useState();
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedTees, setSelectedTees] = useState([]);
  //hooks
  const dispatch = useDispatch();
  const match = useMatch("/:tournamentID/rounds/:roundID/*");
  const navigate = useNavigate();
  const copy = match?.params?.["*"];

  //selectors
  const tournamentRoundNotifications = useSelector(selectRoundNotifications);
  const currentRound = useSelector(selectCurrentRound);
  const currentTournament = useSelector(selectCurrentTournament);
  const tournamentFormatTypes = useSelector(selectTournamentFormatTypes);
  const tournamentRounds = useSelector(selectTournamentRounds);
  const currentFacility = useSelector(selectCurrentFacility);
  const loading = useSelector(selectLoading);

  const tournamentID = currentTournament?.id;
  const firstRoundPro = currentTournament?.singleFormat === "pro_am";
  const hasARound = tournamentRounds.length > 0;

  const [selectedFormatType, setSelectedFormatType] = useState({});

  //the round a linked round was added from, and the selections it fills in
  const { seededRound, seededTees, seededFacility, seededCourses } =
    useLinkedRoundSeed();

  //form context
  const [context, isValid, setIsValid] = useFormValidation();

  context.isCopy = match?.params?.["*"] === "copy";

  //callbacks
  const loadRound = useCallback(() => {
    if (match.params.roundID !== "new") {
      dispatch(getRound(match.params.roundID)).then((response) => {
        const round = response?.payload;
        const tees = round?.tees?.filter?.((t) => t.active) ?? [];
        const roundTees = tees.map?.((t) => ({
          ...t,
          courseID: round.roundCourses?.find?.((c) =>
            c?.tees?.map?.((t) => t.id).includes(t.id)
          )?.id,
        }));
        setSelectedTees(roundTees);
        setSelectedFacility({
          ...round?.facility,
          courses: [...round?.courses],
        });
        if (match?.params?.roundID !== "copy") {
          setSelectedCourses(round?.roundCourses);
        }
      });
    }
  }, [dispatch, match.params.roundID]);

  const formatCanBeChanged = useCallback(() => {
    const singleRound = tournamentRounds?.length === 1;
    const isEdit = match?.params?.roundID !== "new";
    const noTeams = currentTournament?.numberRegisteredTeams === 0;

    return singleRound && isEdit && noTeams;
  }, [match.params.roundID, tournamentRounds, currentTournament]);

  const getCompatibleFormats = useCallback(() => {
    if (!hasARound || formatCanBeChanged()) {
      return tournamentFormatTypes;
    }

    if (!firstRoundPro && hasARound) {
      return tournamentFormatTypes?.filter?.(
        (f) =>
          f.numberOfPlayers ===
            (currentTournament.numberOfPlayers !== 0
              ? currentTournament.numberOfPlayers
              : f.numberOfPlayers) && f.scoringFormat !== "pro_am"
      );
    }

    if (firstRoundPro && hasARound) {
      return tournamentFormatTypes?.filter?.(
        (f) =>
          f.numberOfPlayers ===
            (currentTournament.numberOfPlayers !== 0
              ? currentTournament.numberOfPlayers
              : f.numberOfPlayers) && f.scoringFormat === "pro_am"
      );
    }
  }, [
    tournamentFormatTypes,
    currentTournament.numberOfPlayers,
    firstRoundPro,
    hasARound,
    formatCanBeChanged,
  ]);

  //effects
  useEffect(() => {
    dispatch(getTournamentFormatTypes());
  }, [dispatch]);

  useEffect(() => {
    if (tournamentID) dispatch(getTournamentRounds(tournamentID));
  }, [dispatch, tournamentID]);

  //a copy keeps the round's number and start time so the server sets it up as a
  //sub round of the round it was copied from. the number is still editable, and
  //an edit is what the round is saved with.
  useEffect(() => {
    if (copy === "copy" && currentRound.id) {
      dispatch(setCurrentRound(getLinkedRound(currentRound)));
    }
  }, [dispatch, copy, currentRound]);

  //the copy brings over everything the form requires, so there is a saveable
  //round on the page before the user touches anything. the form fields validate
  //themselves on every render and clear the flag while a selection is missing,
  //so this waits for the ones the copy loads to land.
  const copyChecked = useRef(false);

  useEffect(() => {
    const copyLoaded =
      selectedFacility?.id &&
      selectedCourses?.length > 0 &&
      selectedTees?.length > 0;

    if (copy === "copy" && copyLoaded && !copyChecked.current) {
      copyChecked.current = true;
      setIsValid(isRoundComplete(currentRound));
    }
  }, [
    copy,
    selectedFacility,
    selectedCourses,
    selectedTees,
    currentRound,
    setIsValid,
  ]);

  useEffect(() => {
    if (currentRound.tournamentFormatTypes) {
      setSelectedFormatType(currentRound.tournamentFormatTypes?.[0]);
    }
  }, [currentRound.tournamentFormatTypes]);

  useEffect(() => {
    loadRound();
  }, [loadRound]);

  useEffect(() => {
    return () => {
      dispatch(getTournament(match.params.tournamentID));
    };
  }, [dispatch, match?.params?.tournamentID]);

  useEffect(() => {
    if (
      match?.params?.roundID === "new" &&
      !seededRound?.tournamentFormatTypes
    ) {
      if (tournamentFormatTypes?.length !== 0) {
        let formattypes = getCompatibleFormats();
        let index = formattypes.findIndex((ft) => ft.id === "1");
        setSelectedFormatType(formattypes?.[index !== -1 ? index : 0]);
      }
    }
  }, [
    tournamentFormatTypes,
    getCompatibleFormats,
    match.params.roundID,
    seededRound,
  ]);

  useEffect(() => {
    if (match?.params?.roundID === "new") {
      dispatch(
        setCurrentRound({
          startType: "shotgun_start",
          startAt: "",
          teeTimeInterval: "7/8",
          holeSize: 4,
          ...seededRound,
        })
      );
    }
  }, [dispatch, match?.params?.roundID, seededRound]);

  //loadRound only runs for an existing round, so a seeded round fills the
  //facility, course, and tee selections itself.
  useEffect(() => {
    if (match?.params?.roundID === "new" && seededFacility) {
      setSelectedTees(seededTees);
      setSelectedFacility(seededFacility);
      setSelectedCourses(seededCourses);
      //the copy brought over everything the form requires, so there is a
      //saveable round on the page before the user touches anything.
      setIsValid(isRoundComplete(seededRound));
    }
  }, [
    match?.params?.roundID,
    setIsValid,
    seededRound,
    seededTees,
    seededFacility,
    seededCourses,
  ]);

  useEffect(() => {
    if (currentRound.id) {
      setSelectedCourses(currentRound.roundCourses);
    }
  }, [currentRound.id, currentRound.roundCourses]);

  useEffect(() => {
    if (
      currentRound.startType &&
      selectedFormatType?.numberOfPlayers &&
      selectedFormatType?.availableHoleSizes &&
      !selectedFormatType?.availableHoleSizes?.includes?.(currentRound.holeSize)
    ) {
      let defaultSize =
        selectedFormatType?.numberOfPlayers > 3 &&
        selectedFormatType.availableHoleSizes.includes(
          selectedFormatType.numberOfPlayers
        )
          ? selectedFormatType?.numberOfPlayers
          : selectedFormatType?.availableHoleSizes?.[0];

      context.updateCurrentRound(defaultSize, "holeSize");
    }
  }, [
    selectedFormatType?.numberOfPlayers,
    selectedFormatType?.availableHoleSizes,
    context,
    currentRound.startType,
    currentRound.holeSize,
  ]);

  //context for the form
  //facility
  context.selectFacility = (facility) => {
    if (facility?.id) {
      setIsValid(true);
      setSelectedFacility(facility);
    }
  };

  context.removeFacility = (facility) => {
    setSelectedFacility(false);
    setSelectedTees([]);
    setSelectedCourses([]);
  };

  context.getFacility = () => {
    //the facility's full course list is fetched into the store, so it stands in
    //for the courses the round came in with.
    if (
      currentFacility?.id === selectedFacility?.id &&
      currentFacility?.courses?.length > 0
    ) {
      return { ...selectedFacility, courses: currentFacility.courses };
    }
    return selectedFacility;
  };

  context.hasFacility = () => {
    return selectedFacility?.id !== undefined;
  };

  //course
  context.selectCourse = (course) => {
    setIsValid(true);
    setSelectedCourses([...(selectedCourses ?? []), course]);
  };

  //a round only carries the courses it plays, so removing one starts the course
  //and tee selection over against every course the facility offers.
  context.removeCourse = (course) => {
    setIsValid(true);

    //empty while the facility's courses load, otherwise the course just removed
    //is the only one available and gets picked again on its way out.
    setSelectedFacility({ ...selectedFacility, courses: [] });
    setSelectedCourses([]);
    setSelectedTees([]);

    dispatch(getFacilityCourses(selectedFacility?.id));
  };

  context.holesSelected = (courseID, holes) => {
    setIsValid(true);
    let courseIndex = selectedCourses.findIndex((c) => c.id === courseID);
    let course = { ...selectedCourses?.[courseIndex] };
    course.holesToPlay = holes?.value;
    course.numberOfHoles = holes?.numberOfHoles;
    setSelectedCourses([
      ...selectedCourses.filter((sc) => sc.id !== courseID),
      course,
    ]);
  };

  context.selectedCourses = selectedCourses;

  //tees

  context.addTee = (courseID, tee) => {
    setIsValid(true);
    if (
      !selectedTees.some?.((t) => t.courseID === courseID && tee.id === t.id)
    ) {
      const selectedTs = [...selectedTees, { courseID: courseID, ...tee }];
      setSelectedTees(selectedTs);
    }
  };

  context.removeTee = (courseID, tee) => {
    setIsValid(true);
    setSelectedTees(selectedTees.filter((t) => t.id !== tee.id));
  };

  context.selectedTees = selectedTees;
  context.setSelectedTees = setSelectedTees;

  //tournament
  context.getTournament = () => {
    return currentTournament;
  };

  context.getTournamentFormatTypes = () => {
    return getCompatibleFormats()?.map((type, index) => ({
      label: `${type.name} (${type.numberOfPlayers})`,
      value: type,
    }));
  };

  //round
  context.updateCurrentRound = (value, property) => {
    setIsValid(true);
    let updatedRound = { ...currentRound };
    updatedRound[property] = value;
    dispatch(setCurrentRound(updatedRound));
  };

  context.formatChanged = (type) => {
    setIsValid(true);
    setSelectedFormatType(type.value);
  };

  context.getSelectedFormatType = () => {
    return selectedFormatType;
  };

  context.isCreate = () => {
    return match?.params?.roundID === "new" || match?.params?.["*"] === "copy";
  };

  //save functions

  const save = () => {
    setIsValid(false);
    setUnsaved(false);
    if (match?.params?.roundID === "new" || match?.params?.["*"] === "copy") {
      createNewRound();
    } else {
      let updatedRound = { ...currentRound };
      updatedRound.facility = selectedFacility;
      updatedRound.tournamentFormatTypes = [selectedFormatType];
      updatedRound.startAt = new Date(updatedRound.startAt).toISOString();
      updatedRound.roundCourses = selectedCourses.map((sc, index) => ({
        ...sc,
        tees: [...selectedTees.filter((t) => t.courseID === sc.id)],
        scorecardPosition: index + 1,
      }));
      dispatch(saveRound(updatedRound));
    }
  };

  const createNewRound = () => {
    let updatedRound = { ...currentRound };
    updatedRound.id = undefined;
    updatedRound.state = "draft";
    updatedRound.numberOfHoles = 18;
    //the round saves with the number it was given. one matching an existing
    //round is set up by the server as a sub round of it, which is how the add
    //linked round action and a copy work too. without a number it takes the one
    //after the highest.
    const enteredNumber = parseInt(currentRound.number);
    updatedRound.number =
      enteredNumber > 0
        ? enteredNumber
        : getNextRoundNumber(context?.getTournament?.()?.tournamentRounds);
    updatedRound.facility = { ...selectedFacility };
    updatedRound.tournament = { ...currentTournament };
    updatedRound.tournamentFormatTypes = [selectedFormatType];
    updatedRound.roundCourses = selectedCourses.map((sc, index) => ({
      ...sc,
      id: undefined,
      tees: selectedTees.filter((t) => t.courseID === sc.id),
      scorecardPosition: index + 1,
    }));
    updatedRound.startAt = new Date(updatedRound.startAt).toISOString();
    dispatch(createRound(updatedRound));
  };

  //component functionality
  const leftNavigation = () => {
    if (isValid && !bannerNotifications) {
      setUnsaved(true);
      return;
    }
    navigate(-1);
  };

  const timeoutAction = () => {
    dispatch(setRoundNotifications([]));
  };

  const getActions = () => {
    let actions = [
      {
        name: "Save",
        isDisabled: !isValid,
        action: save,
        type: "black",
      },
      { name: "cancel", action: leftNavigation, type: "grey" },
    ];
    return actions;
  };

  const getNavigation = () => {
    return {
      title: "Round Details",
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const getContent = () => {
    return (
      <GSForm
        formTitle={
          context.isCreate?.()
            ? copy === "copy"
              ? "Copy Round"
              : "Add Round"
            : "Edit Round"
        }
        formSections={getRoundDetailForm(context, currentRound)}
      />
    );
  };

  const getDrawer = () => {
    return {
      actions: getActions(),
    };
  };

  //custom hooks

  //changing a round number can renumber the rounds around it, so the list this
  //page was working from is stale once the save goes through.
  const refreshTournamentRounds = useCallback(() => {
    if (tournamentID) {
      dispatch(getTournamentRounds(tournamentID));
    }
  }, [dispatch, tournamentID]);

  useNotificationAction(
    "saved",
    tournamentRoundNotifications,
    refreshTournamentRounds
  );

  const notificationSettings = {
    notifications: tournamentRoundNotifications,
    bannerActions: getSaveBannerActions(save, leftNavigation),
    timeoutAction,
  };
  useNotificationNavigation(
    "created",
    tournamentRoundNotifications,
    `/${match.params.tournamentID}/rounds/[id]`
  );
  const [bannerNotifications, setUnsaved] = useNotificationBanner(
    notificationSettings
  );

  //view
  return (
    <tournament-round-create-edit>
      <GSSidePanelPage
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
        drawer={getDrawer()}
        loading={loading.length > 0}
      />
    </tournament-round-create-edit>
  );
};

export default TournamentRoundCreateEdit;
